/**
 * AST evaluator for ADI schema boolean expressions.
 *
 * Takes an AstNode (from parser.ts) and a flat form-values object,
 * walks the tree, and returns a boolean result.
 *
 * Field references use dot-notation canonical paths. The evaluator
 * resolves them against the provided values map.
 */

import type { AstNode } from './parser';
import { getTodayISO, yearsBetween, isEmpty, countEntries } from './functions.js';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Evaluate an AST against a flat form-values map.
 *
 * @param node   - AST produced by parseExpression()
 * @param values - Flat key→value map derived from react-hook-form getValues()
 * @returns boolean — true means the condition is satisfied (show the field/section)
 */
export function evaluate(node: AstNode, values: Record<string, unknown>): boolean {
  try {
    const result = evalNode(node, values);
    // Top-level result should be a boolean. If somehow it's a non-boolean
    // (e.g. a bare field reference), coerce it to truthy.
    return Boolean(result);
  } catch (err) {
    console.warn('[schema-renderer/evaluator] Evaluation error:', err);
    // On error, default to showing the field (fail open) so nothing is
    // accidentally hidden from the user.
    return true;
  }
}

// ---------------------------------------------------------------------------
// Internal recursive evaluator
// ---------------------------------------------------------------------------

function evalNode(node: AstNode, values: Record<string, unknown>): unknown {
  switch (node.kind) {
    case 'BinaryOp': {
      if (node.op === 'AND') {
        return Boolean(evalNode(node.left, values)) && Boolean(evalNode(node.right, values));
      }
      // OR
      return Boolean(evalNode(node.left, values)) || Boolean(evalNode(node.right, values));
    }

    case 'Not': {
      return !evalNode(node.operand, values);
    }

    case 'Comparison': {
      const left = evalNode(node.left, values);
      const right = evalNode(node.right, values);
      return evalComparison(node.op, left, right);
    }

    case 'FieldRef': {
      return resolveField(node.path, values);
    }

    case 'StringLiteral': {
      return node.value;
    }

    case 'NumberLiteral': {
      return node.value;
    }

    case 'ListLiteral': {
      return node.items.map((item) => evalNode(item, values));
    }

    case 'FunctionCall': {
      return evalFunction(node.name, node.args, values);
    }

    default: {
      const exhaustive: never = node;
      console.warn('[schema-renderer/evaluator] Unknown AST node kind:', exhaustive);
      return null;
    }
  }
}

// ---------------------------------------------------------------------------
// Comparison dispatch
// ---------------------------------------------------------------------------

function evalComparison(
  op: '==' | '!=' | '>' | '<' | '>=' | '<=' | 'IN' | 'NOT_IN' | 'MATCHES',
  left: unknown,
  right: unknown,
): boolean {
  switch (op) {
    case '==':
      return looseEqual(left, right);
    case '!=':
      return !looseEqual(left, right);
    case '>':
      return toNumber(left) > toNumber(right);
    case '<':
      return toNumber(left) < toNumber(right);
    case '>=':
      return toNumber(left) >= toNumber(right);
    case '<=':
      return toNumber(left) <= toNumber(right);
    case 'IN': {
      const list = toList(right);
      // multi_enum fields resolve to an array of selected values (e.g.
      // sponsor.home.occupants_relationships == ["Sibling", "Other"]). "field
      // IN [...]" for those means "selection intersects the list", not a
      // scalar match — matching left against list as a whole via looseEqual's
      // Array.toString() coercion only ever worked by accident for a
      // single-element selection, and silently broke for any other selection
      // count (2026-07-13 fix, GATE-04 / 40SP occupants_relationships_other).
      if (Array.isArray(left)) {
        return left.some((l) => list.some((item) => looseEqual(l, item)));
      }
      return list.some((item) => looseEqual(left, item));
    }
    case 'NOT_IN': {
      const list = toList(right);
      if (Array.isArray(left)) {
        return !left.some((l) => list.some((item) => looseEqual(l, item)));
      }
      return !list.some((item) => looseEqual(left, item));
    }
    case 'MATCHES': {
      if (typeof left !== 'string' || typeof right !== 'string') return false;
      try {
        return new RegExp(right).test(left);
      } catch {
        return false;
      }
    }
    default: {
      const exhaustive: never = op;
      console.warn('[schema-renderer/evaluator] Unknown operator:', exhaustive);
      return false;
    }
  }
}

// ---------------------------------------------------------------------------
// Function call dispatch
// ---------------------------------------------------------------------------

function evalFunction(
  name: 'TODAY' | 'YEARS_BETWEEN' | 'IS_EMPTY' | 'COUNT',
  args: AstNode[],
  values: Record<string, unknown>,
): unknown {
  switch (name) {
    case 'TODAY':
      return getTodayISO();

    case 'YEARS_BETWEEN': {
      const d1 = evalNode(args[0], values);
      const d2 = evalNode(args[1], values);
      return yearsBetween(d1, d2);
    }

    case 'IS_EMPTY': {
      const val = evalNode(args[0], values);
      return isEmpty(val);
    }

    case 'COUNT': {
      const val = evalNode(args[0], values);
      return countEntries(val);
    }

    default: {
      const exhaustive: never = name;
      console.warn('[schema-renderer/evaluator] Unknown function:', exhaustive);
      return null;
    }
  }
}

// ---------------------------------------------------------------------------
// Field resolution
// ---------------------------------------------------------------------------

/**
 * Resolve a dot-notation canonical path against the flat values map.
 *
 * react-hook-form stores nested values as a tree. The incoming values may be:
 *   a) A flat map: { "applicant.dob": "1990-01-01" }
 *   b) A nested object: { applicant: { dob: "1990-01-01" } }
 *
 * We try the flat key first, then walk the nested object.
 *
 * For repeater paths like "applicant.children[*].is_migrating", we return
 * the array of values across all entries (used with COUNT/IN).
 */
function resolveField(path: string, values: Record<string, unknown>): unknown {
  // Direct flat lookup first
  if (Object.prototype.hasOwnProperty.call(values, path)) {
    return values[path];
  }

  // Nested object walk
  const segments = splitPath(path);
  let current: unknown = values;
  for (const seg of segments) {
    if (current === null || current === undefined) return undefined;
    if (seg === '[*]') {
      // Wildcard: collect values from all array entries
      if (!Array.isArray(current)) return [];
      return current;
    }
    if (typeof current === 'object' && !Array.isArray(current)) {
      current = (current as Record<string, unknown>)[seg];
    } else {
      return undefined;
    }
  }
  return current;
}

/**
 * Split a canonical path like "applicant.children[*].dob" into segments:
 * ["applicant", "children", "[*]", "dob"]
 */
function splitPath(path: string): string[] {
  // Replace [*] with .[*] so we can split on dots, then clean up
  return path
    .replace(/\[\*\]/g, '.[*]')
    .split('.')
    .filter(Boolean);
}

// ---------------------------------------------------------------------------
// Type coercion helpers
// ---------------------------------------------------------------------------

function looseEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  // Coerce to string for comparison — form values are often strings
  return String(a) === String(b);
}

function toNumber(v: unknown): number {
  if (typeof v === 'number') return v;
  const n = parseFloat(String(v));
  return isNaN(n) ? 0 : n;
}

function toList(v: unknown): unknown[] {
  if (Array.isArray(v)) return v;
  return [v];
}
